import type {
  APIConfig,
  BigbrotherClient,
  CinnamonClient,
  DerekClient,
  HotelClient,
  Logger,
} from '@/apis/infrastructures'
import { APIError } from '@/libs/utilities/CustomErrors'
import router, { createLoginRedirectHref, isRequireAuthRoute } from '@/router'
import { createDolphinLogger } from '@400f/ltl-people'
import { captureException } from '@sentry/vue'
import type { AxiosError, AxiosInstance } from 'axios'
import axios from 'axios'
const setAPIErrorInterceptor = (instance: AxiosInstance): AxiosInstance => {
  instance.interceptors.response.use(undefined, (error: AxiosError) => {
    return Promise.reject(
      new APIError(
        error?.response?.status ?? 0,
        JSON.stringify(error.response?.data || error.message)
      )
    )
  })
  return instance
}

const redirectAuthInterceptor = (instance: AxiosInstance): AxiosInstance => {
  instance.interceptors.response.use(undefined, (error: APIError) => {
    const currentRoute = router.currentRoute.value
    if (error.isUnauthorized && isRequireAuthRoute(currentRoute)) {
      location.href = createLoginRedirectHref(
        currentRoute.path,
        currentRoute.query
      )
    }
    return Promise.reject(error)
  })
  return instance
}

export const createCinnamonClient = (config: APIConfig): CinnamonClient => {
  const { cinnamonUrl, apiToken } = config
  return redirectAuthInterceptor(
    setAPIErrorInterceptor(
      axios.create({
        baseURL: cinnamonUrl,
        headers: {
          ...(apiToken && { 'X-API-TOKEN': apiToken.toString() }),
        },
      })
    )
  )
}

export const createDerekClient = (config: APIConfig): DerekClient => {
  const { derekUrl, apiToken } = config
  return redirectAuthInterceptor(
    setAPIErrorInterceptor(
      axios.create({
        baseURL: derekUrl,
        headers: {
          ...(apiToken && { 'X-API-TOKEN': apiToken.toString() }),
          'Content-Type': 'application/json; charset=utf-8',
          Accept: 'application/json, text/plain, */*',
        },
        withCredentials: true,
      })
    )
  )
}

export const createBigbrotherClient = (config: APIConfig): BigbrotherClient => {
  const { bigbrotherUrl, apiToken } = config
  return redirectAuthInterceptor(
    setAPIErrorInterceptor(
      axios.create({
        baseURL: bigbrotherUrl,
        headers: {
          ...(apiToken && { 'X-API-TOKEN': apiToken.toString() }),
          'Content-Type': 'application/json; charset=utf-8',
          Accept: 'application/json, text/plain, */*',
        },
        withCredentials: true,
      })
    )
  )
}

export const createHotelClient = (config: APIConfig): HotelClient => {
  const { hotelUrl, apiToken } = config
  return redirectAuthInterceptor(
    setAPIErrorInterceptor(
      axios.create({
        baseURL: hotelUrl,
        headers: {
          ...(apiToken && { 'X-API-TOKEN': apiToken.toString() }),
          'Content-Type': 'application/json; charset=utf-8',
          Accept: 'application/json, text/plain, */*',
        },
      })
    )
  )
}

export const createLogger = (config: APIConfig): Logger => {
  const { bigbrotherUrl, bigbrotherV2Url, env, committedTime } = config
  return createDolphinLogger({
    env,
    baseUrl: bigbrotherUrl,
    onError: e => {
      captureException(e)
    },
    v2: {
      baseUrl: bigbrotherV2Url,
      serviceLastCommittedAt: committedTime,
    },
  })['logger']
}
